import React, { useEffect, useCallback } from "react";
import cns from "classnames";
import { Observer } from "mobx-react";
import { Paper, withStyles } from "@material-ui/core";
import { AutoSizer, defaultTableRowRenderer, TableRowRenderer } from "react-virtualized";

import { Classes } from "@theme";
import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchResultLayout from "@search/components/SearchResultLayout";
import SearchOptions from "@search/SearchOptions";
import TableSearch from "@search/TableSearch/TableSearch";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSubheader from "@search/TableSearch/TableSubheader";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";

import { BookingAuditSearchStore } from "../BookingAuditSearch/BookingAuditSearchStore";
import { getColumns } from "./BookingAuditSearchColumns";
import { BookingSearchForm } from "../BookingSearchForm";
import { RowType } from "./utils";
import { BookingFilterGroup, groupBy } from "../utils";
import { getPollLimit } from "../poll-limit";
import { BookingSearchActionBar } from "../BookingSearchActionBar";
import { usePolling } from "@bonliva-crm/ui";

type Props = {
  onOpen: (id: string, tab?: string, scrollContainer?: HTMLElement) => void;
  onEdit: (id?: string) => void;
  onGroupBy: (groupBy: BookingFilterGroup) => void;
  classes: Classes<typeof styles>;
};

const BookingAuditSearch: React.FC<Props> = ({ classes, onEdit, onGroupBy }) => {
  const store = React.useMemo(() => new BookingAuditSearchStore(), []);

  useEffect(() => {
    return () => {
      store.unsubscribe();
    };
  }, [store]);

  usePolling(
    async () => {
      await store.queryFuncWatch.refetch({
        filter: store.filterManager.queryInput,
        limit: getPollLimit(store.searchManager.items?.length),
        skip: 0
      });
    },
    "/bookings",
    { interval: 5000, refetchOnPushState: true }
  );

  const columns = React.useMemo(() => getColumns(store), [store]);

  const renderFilter = useCallback(() => {
    return (
      <div>
        <BookingSearchForm
          manager={store.filterManager}
          group={BookingFilterGroup.Audit}
        />
      </div>
    );
  }, [store.filterManager]);

  const renderGroupHeadline = useCallback(() => {
    const { searchManager } = store;

    return (
      <Observer>
        {() => {
          if (!searchManager.header) {
            return null;
          }

          return (
            <TableSubheader
              center
              mainText={searchManager.header.week}
              smallText={searchManager.header.year}
            />
          );
        }}
      </Observer>
    );
  }, [store.searchManager]);

  const rowRenderer: TableRowRenderer = useCallback(
    (rowProps) => {
      const row: RowType = rowProps.rowData;

      if (row.type === "group") {
        return (
          <div
            className={cns(rowProps.className, classes.subHeader)}
            aria-rowindex={rowProps.index}
            key={row._id}
            role="row"
            style={{
              ...rowProps.style,
              width: rowProps.style.width - 1
            }}
          >
            <TableSubheader
              center
              mainText={row.header.week}
              smallText={row.header.year}
            />
          </div>
        );
      }

      return defaultTableRowRenderer({
        ...rowProps,
        rowData: row.data
      });
    },
    [classes.subHeader]
  );

  const getRowClassName = useCallback(
    (opts: { index: number }) => {
      const item = store.searchManager.getRow(opts.index);

      if (item.type !== "card") {
        return;
      }

      const isOdd = opts.index % 2 !== 1;

      return cns({ odd: isOdd });
    },
    [store.searchManager]
  );

  return (
    <SearchPageLayout
      delayed
      wrapContent={(content) => <Paper>{content}</Paper>}
      actionBar={
        <Observer>
          {() => (
            <>
              <BookingSearchActionBar onDirectBooking={() => onEdit(null)} />

              <SearchOptions
                activeGroupBy={BookingFilterGroup.Audit}
                groupBy={groupBy()}
                onGroupBy={(v) => onGroupBy(v.value)}
              />
            </>
          )}
        </Observer>
      }
      content={(scrollContainer) => (
        <SearchResultLayout fittedContent fitted style={{ maxWidth: 9999 }}>
          <AutoSizer disableHeight className={classes.stickyHeader}>
            {({ width }) => (
              <div style={{ background: "white", position: "relative", width }}>
                <ColumnSizer columns={columns} width={width}>
                  {({ ref }) => (
                    <TableSearchHeader
                      columns={columns}
                      columnSizer={ref}
                      style={{ backgroundColor: "#fff" }}
                    />
                  )}
                </ColumnSizer>
                <div style={{ width }}>{renderGroupHeadline()}</div>
              </div>
            )}
          </AutoSizer>

          <div>
            <TableSearch
              disableHeader
              dynamicColIndex={3}
              scrollContainer={scrollContainer}
              manager={store.searchManager}
              columns={columns}
              rowClassName={getRowClassName}
              rowHeight={({ index }) => {
                const row = store.searchManager.getRow(index);
                return row.type === "group" ? 48 : 56;
              }}
              rowRenderer={rowRenderer}
              rowStyle={{ cursor: "pointer" }}
              onRowsRendered={store.searchManager.onRowsRendered}
            />
          </div>
        </SearchResultLayout>
      )}
      filter={renderFilter()}
    />
  );
};

const styles = () => ({
  subHeader: { display: "flex", height: 48, pointerEvents: "none" },
  stickyHeader: { position: "sticky", top: 0, zIndex: 1 },
  shadow: {
    position: "absolute",
    bottom: 0,
    top: 10,
    left: 4,
    right: 4,
    zIndex: -1,
    boxShadow: "0 0 4px gray"
  }
});

export default withStyles(styles as any)(BookingAuditSearch);
